import { create } from "zustand";
import type { ArrowCorner, ConnectorSide, StampKind, TextAlign } from "@refinement-canvas/domain";
import type { ViewState } from "../../../lib/canvas-coordinates";
import type { MarqueeRect } from "../../../lib/marquee";
import { loadGridSnapPreference, saveGridSnapPreference } from "../../../lib/grid-snap-preference";
import {
  loadSelectPanInvertPreference,
  saveSelectPanInvertPreference,
} from "../../../lib/select-pan-invert-preference";
import {
  loadPenColor,
  loadPenThickness,
  savePenThickness,
  type PenColor,
  type PenThickness,
} from "../../../lib/pen-preference";
import { loadStickyColor, type StickyColor } from "../../../lib/sticky-preference";
import { loadShapeFill, type ShapeFill } from "../../../lib/shape-preference";
import { loadSectionFill, saveSectionFill } from "../../../lib/section-preference";
import { loadTextFontSize, saveTextFontSize } from "../../../lib/text-size-preference";
import { loadTextAlign, saveTextAlign } from "../../../lib/text-align-preference";

// キャンバス UI のローカル状態を管理する Zustand store。
// pan/zoom、選択中ツール、選択中オブジェクトなど高頻度更新の値を保持する。

export type CanvasTool =
  | "idle"
  | "panning"
  | "place-sticky"
  | "place-section"
  | "place-shape"
  | "place-text"
  | "draw-pen"
  | "place-stamp"
  | "place-comment";

export type ShapeKind = "circle" | "rectangle" | "arrow";

/**
 * 「既存オブジェクトの上でも新規配置を通す」配置ツールか（付箋/図形/テキスト/スタンプ/コメント）。
 * 既存オブジェクト本体・コネクタ・選択ハンドル等が pointerdown を止めずに stage 配置へ譲るかの判定を
 * 1 箇所へ集約する（経路ごとに許可リストが重複して追従漏れが起きるのを防ぐ）。新しい配置ツールを
 * 追加するときは、この述語だけを更新すれば全経路へ反映される。
 */
export function isObjectPlacementTool(tool: CanvasTool): boolean {
  return (
    tool === "place-sticky" ||
    tool === "place-section" ||
    tool === "place-shape" ||
    tool === "place-text" ||
    tool === "place-stamp" ||
    tool === "place-comment"
  );
}

/**
 * 既存オブジェクトの上でもカーソルを crosshair（新規作成・描画の意思表示）にすべきツールか。
 * 配置ツール（付箋/図形/テキスト/スタンプ/コメント）に加えて、ペン描画（draw-pen）も含める。
 * ペンは既存オブジェクトの上にも描けるが、object 側の cursor が `isObjectPlacementTool` だけを
 * 見ていると draw-pen が move のままになり「移動できる」と誤認させてしまう（特に図形は内側 SVG が
 * pointerEvents:all のため SVG の cursor を継承して move になる）。カーソル表示専用の判定として
 * これを使い、pointerdown の配置ルーティングに使う `isObjectPlacementTool` とは分離する。
 */
export function usesCrosshairCursor(tool: CanvasTool): boolean {
  return isObjectPlacementTool(tool) || tool === "draw-pen";
}

export interface PointerPosition {
  readonly x: number;
  readonly y: number;
}

/** 右クリックで開くコンテキストメニューの状態（クライアント座標）。 */
export interface ContextMenuState {
  readonly objectId: string;
  readonly clientX: number;
  readonly clientY: number;
}

/**
 * 何もない空白部を右クリックしたときに開くコンテキストメニューの状態。
 * clientX/Y はメニューの表示位置（画面座標）、stageX/Y は貼り付け先のステージ座標
 * （開いた瞬間の右クリック位置を保持し、以後のマウス移動で貼り付け先がずれないようにする）。
 */
export interface EmptyContextMenuState {
  readonly clientX: number;
  readonly clientY: number;
  readonly stageX: number;
  readonly stageY: number;
}

/**
 * コネクタ（接続矢印）作成中の一時状態。
 * 接続元（fromObjectId / fromSide）からポインタ位置（ステージ座標）へゴースト線を引き、
 * 接続先候補が見つかれば target に保持して 4 辺ドットを表示・スナップする。
 */
export interface ConnectionDraftState {
  readonly fromObjectId: string;
  readonly fromSide: ConnectorSide;
  readonly pointer: PointerPosition;
  readonly target: { readonly objectId: string; readonly side: ConnectorSide } | null;
}

/**
 * 図形をドラッグで配置している最中のプレビュー枠（ステージ座標）。
 * ドラッグしていないときは null。確定（pointerup）時にこのサイズで図形を作成する。
 * 矢印のときは arrowFromCorner にドラッグ方向に応じた起点角を保持する。
 */
export interface ShapePlacementDraft {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly arrowFromCorner?: ArrowCorner | undefined;
}

/**
 * コメント配置（place-comment）でキャンバスをクリックした位置（ステージ座標）。
 * この値がセットされている間、その位置にコメント入力ウィンドウを表示する。
 * 投稿確定・キャンセル・モードリセットで null に戻す。
 */
export interface CommentDraftState {
  readonly x: number;
  readonly y: number;
}

interface CanvasUiState {
  view: ViewState;
  tool: CanvasTool;
  shapeKind: ShapeKind;
  /** スタンプ配置時に使う絵文字種別。place-stamp ツール時のサブ選択結果を保持する。 */
  stampKind: StampKind;
  penColor: PenColor;
  /** ペンの太さ（細 / 中 / 太）。描画時の線幅（PEN_THICKNESS_PIXELS）へ変換して使う。 */
  penThickness: PenThickness;
  stickyColor: StickyColor;
  /** 図形配置で使用する背景色（fill）。"none"（背景なし）または hex 文字列を保持する。 */
  shapeFill: ShapeFill;
  /** セクション配置で使用する背景色（fill）。常に hex 文字列（「背景なし」は提供しない）。 */
  sectionFill: string;
  /** テキスト配置で使用する初期 font-size（px 値）。プリセット（小/中/大）・px 指定の両方を保持する。 */
  textFontSize: number;
  /** テキスト・付箋配置で使用する初期の文字寄せ（左/中央/右）。既定は左寄せ。 */
  textAlign: TextAlign;
  selectedObjectIds: readonly string[];
  /**
   * 配置直後に自動で編集モードへ入れたいオブジェクトの id。
   * テキスト配置（place-text）で空のテキスト図形を作った直後にセットし、対象の TextObject が
   * マウント時に編集モードへ入って即入力できるようにする。処理後は対象コンポーネントが null に戻す。
   */
  autoEditObjectId: string | null;
  /**
   * 範囲指定（マーキー）選択のドラッグ中に表示する選択枠（ステージ座標）。
   * ドラッグしていないときは null。確定時に枠へ重なるオブジェクトを選択する。
   */
  marqueeRect: MarqueeRect | null;
  lastPointerStagePosition: PointerPosition | null;
  /** グリッド吸着モード（ON のとき、ペン以外のオブジェクトは移動・リサイズ・配置時に CANVAS_SNAP_PX へ丸める）。 */
  gridSnapEnabled: boolean;
  /**
   * 選択操作の左右反転モード（既定 OFF）。
   * OFF: 空白部の左ドラッグ=キャンバス移動 / 右ドラッグ=範囲選択（従来）。
   * ON : 空白部の右ドラッグ=キャンバス移動 / 左ドラッグ=範囲選択（反転）。
   * どちらのモードでも図形選択は左シングルクリック、図形コンテキストメニューは右シングルクリックのまま。
   */
  invertSelectPan: boolean;
  contextMenu: ContextMenuState | null;
  /** 空白部の右クリックで開く「貼り付け」コンテキストメニューの状態。開いていないときは null。 */
  emptyContextMenu: EmptyContextMenuState | null;
  /** コネクタ作成中の一時状態。作成していないときは null。 */
  connectionDraft: ConnectionDraftState | null;
  /** 図形をドラッグ配置中のプレビュー枠。ドラッグしていないときは null。 */
  shapePlacementDraft: ShapePlacementDraft | null;
  /** コメント入力ウィンドウの表示位置（新規スレッド配置中）。未配置時は null。 */
  commentDraft: CommentDraftState | null;
  /**
   * 参照専用モード（Connect Note からの埋め込み等）。true の間は編集系の入力・操作
   * （オブジェクト配置・移動・リサイズ・回転・削除・貼り付け・編集ショートカット・コメント投稿）を
   * 無効化し、パン / ズーム / リンク遷移 / コメント閲覧のみを許可する。
   */
  readOnly: boolean;

  /**
   * view 同期（表示位置の保存 / URL 反映）の arm 要求シグナル（単調増加カウンタ）。
   * canvas-stage の arming は canvas-container 内のホイール / pointerdown / キー操作に限定されるため、
   * その外側（コメント UI 上の Ctrl+ホイールズーム等）で view を動かした操作を canvas-stage へ伝える。
   * requestViewSyncArm でインクリメントし、canvas-stage 側が変化を検知して arm する。
   */
  viewSyncArmSignal: number;

  setView: (view: ViewState) => void;
  /** view 同期の arm を要求する（viewSyncArmSignal をインクリメント）。 */
  requestViewSyncArm: () => void;
  setTool: (tool: CanvasTool) => void;
  setShapeKind: (shape: ShapeKind) => void;
  setStampKind: (kind: StampKind) => void;
  setPenColor: (color: PenColor) => void;
  setPenThickness: (thickness: PenThickness) => void;
  setStickyColor: (color: StickyColor) => void;
  setShapeFill: (fill: ShapeFill) => void;
  setSectionFill: (fill: string) => void;
  setTextFontSize: (px: number) => void;
  setTextAlign: (align: TextAlign) => void;
  setSelectedObjectIds: (ids: readonly string[]) => void;
  setAutoEditObjectId: (id: string | null) => void;
  setMarqueeRect: (rect: MarqueeRect | null) => void;
  setLastPointerStagePosition: (point: PointerPosition | null) => void;
  setGridSnapEnabled: (enabled: boolean) => void;
  setInvertSelectPan: (enabled: boolean) => void;
  openContextMenu: (state: ContextMenuState) => void;
  closeContextMenu: () => void;
  openEmptyContextMenu: (state: EmptyContextMenuState) => void;
  closeEmptyContextMenu: () => void;
  setConnectionDraft: (draft: ConnectionDraftState | null) => void;
  setShapePlacementDraft: (draft: ShapePlacementDraft | null) => void;
  setCommentDraft: (draft: CommentDraftState | null) => void;
  setReadOnly: (readOnly: boolean) => void;
  resetMode: () => void;
}

export const useCanvasStore = create<CanvasUiState>((set) => ({
  view: { panX: 0, panY: 0, zoom: 1 },
  tool: "idle",
  shapeKind: "rectangle",
  // スタンプは最初の選択肢を既定にしておく（明示的に切替するまで「いいね」スタンプが配置される）。
  stampKind: "thumbs-up",
  penColor: loadPenColor(),
  penThickness: loadPenThickness(),
  stickyColor: loadStickyColor(),
  shapeFill: loadShapeFill(),
  sectionFill: loadSectionFill(),
  textFontSize: loadTextFontSize(),
  textAlign: loadTextAlign(),
  selectedObjectIds: [],
  autoEditObjectId: null,
  marqueeRect: null,
  lastPointerStagePosition: null,
  gridSnapEnabled: loadGridSnapPreference(),
  invertSelectPan: loadSelectPanInvertPreference(),
  contextMenu: null,
  emptyContextMenu: null,
  connectionDraft: null,
  shapePlacementDraft: null,
  commentDraft: null,
  readOnly: false,
  viewSyncArmSignal: 0,

  setView: (view) => set({ view }),
  setReadOnly: (readOnly) => set({ readOnly }),
  requestViewSyncArm: () => set((state) => ({ viewSyncArmSignal: state.viewSyncArmSignal + 1 })),
  setTool: (tool) => set({ tool }),
  setShapeKind: (shapeKind) => set({ shapeKind }),
  setStampKind: (stampKind) => set({ stampKind }),
  setPenColor: (penColor) => set({ penColor }),
  setPenThickness: (penThickness) => {
    // 色サイズと同様、次回の描画時に再現できるようローカルストレージへ永続化する。
    savePenThickness(penThickness);
    set({ penThickness });
  },
  setStickyColor: (stickyColor) => set({ stickyColor }),
  setShapeFill: (shapeFill) => set({ shapeFill }),
  setSectionFill: (sectionFill) => {
    // 図形・付箋と同様、次回の配置時に再現できるようローカルストレージへ永続化する。
    saveSectionFill(sectionFill);
    set({ sectionFill });
  },
  setTextFontSize: (px) => {
    // ローカルストレージにも永続化し、次回の挿入時に再現できるようにする。
    saveTextFontSize(px);
    set({ textFontSize: px });
  },
  setTextAlign: (align) => {
    // 文字サイズと同様、次回の挿入時に再現できるようローカルストレージへ永続化する。
    saveTextAlign(align);
    set({ textAlign: align });
  },
  setSelectedObjectIds: (selectedObjectIds) => set({ selectedObjectIds }),
  setAutoEditObjectId: (autoEditObjectId) => set({ autoEditObjectId }),
  setMarqueeRect: (marqueeRect) => set({ marqueeRect }),
  setLastPointerStagePosition: (lastPointerStagePosition) => set({ lastPointerStagePosition }),
  setGridSnapEnabled: (enabled) => {
    saveGridSnapPreference(enabled);
    set({ gridSnapEnabled: enabled });
  },
  setInvertSelectPan: (enabled) => {
    // グリッド吸着と同様、次回の起動時に再現できるようローカルストレージへ永続化する。
    saveSelectPanInvertPreference(enabled);
    set({ invertSelectPan: enabled });
  },
  // オブジェクトメニューと空白部メニューは相互排他（片方を開くともう片方を閉じる）。
  openContextMenu: (contextMenu) => set({ contextMenu, emptyContextMenu: null }),
  closeContextMenu: () => set({ contextMenu: null }),
  openEmptyContextMenu: (emptyContextMenu) => set({ emptyContextMenu, contextMenu: null }),
  closeEmptyContextMenu: () => set({ emptyContextMenu: null }),
  setConnectionDraft: (connectionDraft) => set({ connectionDraft }),
  setShapePlacementDraft: (shapePlacementDraft) => set({ shapePlacementDraft }),
  setCommentDraft: (commentDraft) => set({ commentDraft }),
  resetMode: () =>
    set({
      tool: "idle",
      selectedObjectIds: [],
      contextMenu: null,
      emptyContextMenu: null,
      marqueeRect: null,
      connectionDraft: null,
      shapePlacementDraft: null,
      commentDraft: null,
    }),
}));
